import { supabase } from '../../../shared/lib/supabase';
import type {
  BillingPricingRule,
  BillingPricingRuleCondition,
  BillingPricingRuleConditionCreateInput,
  BillingPricingRuleCreateInput,
} from './billingTypes';

export class BillingPricingRulesService {
  private rulesTable = 'billing_pricing_rules';
  private conditionsTable = 'billing_pricing_rule_conditions';

  async getAll(): Promise<BillingPricingRule[]> {
    const { data, error } = await supabase
      .from(this.rulesTable)
      .select('*')
      .order('priority', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching billing pricing rules:', error);
      throw error;
    }

    return data || [];
  }

  async create(input: BillingPricingRuleCreateInput): Promise<BillingPricingRule> {
    const payload: BillingPricingRuleCreateInput = {
      priority: 100,
      is_active: true,
      valid_from: new Date().toISOString(),
      ...input,
    };

    const { data, error } = await supabase.from(this.rulesTable).insert([payload]).select().single();

    if (error) {
      console.error('Error creating billing pricing rule:', error);
      throw error;
    }

    return data;
  }

  async update(id: string, input: Partial<BillingPricingRuleCreateInput>): Promise<BillingPricingRule> {
    const { data, error } = await supabase
      .from(this.rulesTable)
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating billing pricing rule:', error);
      throw error;
    }

    return data;
  }

  async getConditions(ruleId: string): Promise<BillingPricingRuleCondition[]> {
    const { data, error } = await supabase
      .from(this.conditionsTable)
      .select('*')
      .eq('billing_pricing_rule_id', ruleId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching billing pricing rule conditions:', error);
      throw error;
    }

    return data || [];
  }

  async createCondition(input: BillingPricingRuleConditionCreateInput): Promise<BillingPricingRuleCondition> {
    const { data, error } = await supabase.from(this.conditionsTable).insert([input]).select().single();

    if (error) {
      console.error('Error creating billing pricing rule condition:', error);
      throw error;
    }

    return data;
  }

  async deleteCondition(id: string): Promise<void> {
    const { error } = await supabase.from(this.conditionsTable).delete().eq('id', id);
    if (error) {
      console.error('Error deleting billing pricing rule condition:', error);
      throw error;
    }
  }

  async updateCondition(
    id: string,
    input: Partial<Pick<BillingPricingRuleCondition, 'field_key' | 'operator' | 'field_value'>>
  ): Promise<BillingPricingRuleCondition> {
    const { data, error } = await supabase
      .from(this.conditionsTable)
      .update(input)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      console.error('Error updating billing pricing rule condition:', error);
      throw error;
    }
    return data;
  }

  async deleteRule(id: string): Promise<void> {
    const { error } = await supabase.from(this.rulesTable).delete().eq('id', id);
    if (error) {
      console.error('Error deleting billing pricing rule:', error);
      throw error;
    }
  }
}

export const billingPricingRulesService = new BillingPricingRulesService();
